/**
 * Week descriptions and attachments under Live Übersicht (PRD §4.4/§4.5).
 */

import { useTranslation } from "react-i18next";
import { Download, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, saveBlob } from "@/lib/api";
import { formatPersonName } from "@/lib/format";
import { useProjectWeekNotes } from "@/lib/tracking";

export function ProjectWeekNotesSection({ projectId, year }: { projectId: string; year: number }) {
  const { t, i18n } = useTranslation();
  const notes = useProjectWeekNotes(projectId, year);
  const entries = notes.data ?? [];

  async function downloadAttachment(userId: string, attachmentId: string, filename: string) {
    const { blob } = await api.download(
      `/users/${userId}/projects/${projectId}/attachments/${attachmentId}`,
    );
    saveBlob(blob, filename);
  }

  return (
    <section
      className="mt-8 rounded-2xl border border-border bg-card p-5"
      data-testid="live-week-notes"
    >
      <h3 className="flex items-center gap-2 font-medium">
        <FileText className="size-4" /> {t("projectLive.weekNotesTitle")}
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">
        {t("projectLive.weekNotesSubtitle", { year })}
      </p>

      {notes.isLoading ? (
        <p className="mt-4 text-sm text-muted-foreground">{t("common.loading")}</p>
      ) : notes.isError ? (
        <p className="mt-4 text-sm text-destructive" data-testid="live-week-notes-error">
          {t("projectLive.weekNotesError")}
        </p>
      ) : entries.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground" data-testid="live-week-notes-empty">
          {t("projectLive.weekNotesEmpty", { year })}
        </p>
      ) : (
        <div className="mt-4 max-h-[700px] overflow-auto" data-testid="live-week-notes-table">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-card">
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th className="p-2">{t("admin.weekNotesColWeek")}</th>
                <th className="p-2">{t("admin.weekNotesColEmployee")}</th>
                <th className="p-2">{t("admin.weekNotesColDescription")}</th>
                <th className="p-2">{t("admin.weekNotesColAttachments")}</th>
                <th className="whitespace-nowrap p-2">{t("admin.weekNotesColUpdated")}</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((note) => (
                <tr key={note.id} className="border-b border-border/40 align-top">
                  <td className="whitespace-nowrap p-2 font-medium">
                    {note.year}-W{String(note.isoWeek).padStart(2, "0")}
                  </td>
                  <td className="whitespace-nowrap p-2">
                    <div>{formatPersonName(note)}</div>
                    <div className="text-[10px] text-muted-foreground">{note.email}</div>
                  </td>
                  <td className="max-w-xl whitespace-pre-wrap p-2">{note.note || "—"}</td>
                  <td className="max-w-xs p-2">
                    {note.attachments.length === 0 ? (
                      "—"
                    ) : (
                      <div className="flex flex-col gap-1">
                        {note.attachments.map((file) => (
                          <Button
                            key={file.id}
                            type="button"
                            variant="link"
                            size="sm"
                            className="h-auto justify-start px-0 text-left"
                            onClick={() =>
                              void downloadAttachment(note.userId, file.id, file.filename)
                            }
                          >
                            <Download className="size-3.5 shrink-0" />
                            <span className="truncate">{file.filename}</span>
                          </Button>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="whitespace-nowrap p-2 text-xs text-muted-foreground">
                    {new Date(note.updatedAt).toLocaleString(
                      i18n.language === "en" ? "en-US" : "de-DE",
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
